/**
 * Descriptor-driven setting rows, built from the kit: known options are the
 * compact label / value / chevron PickerRow, booleans a Switch (with an
 * explicit Unknown and Clear when nullable), everything else an inline Field
 * with pounds, percentage or unit affixes. Edits are reported as exact API
 * values; an invalid edit stays visible and is rejected at Save, never rounded.
 */
import { useEffect, useId, useState, type ReactNode } from 'react'
import type { DealModelValueDescriptor } from '../../api/dealModelTypes'
import { Button, Field, Info, NumberInput, PickerRow, Switch, TextInput } from '../../ui/kit'
import { DateInput } from '../../ui/DateInput'
import {
  formatSettingsMoney, settingsDisplayToValue, settingsFieldPresentation, settingsOptionLabel, settingsValueToDisplay,
  type SettingsFieldPresentation, type SettingsValue,
} from './settingsFieldPresentation'

export function SettingsGroup({ title, description, children }: { title: string; description?: string; children: ReactNode }) {
  return (
    <section className="panel set-group" aria-label={title}>
      <header className="set-group-h"><h2 className="t-section">{title}</h2>{description ? <Info text={description} /> : null}</header>
      <div className="set-rows">{children}</div>
    </section>
  )
}

export interface SettingsFieldProps {
  descriptor: DealModelValueDescriptor
  value: SettingsValue | undefined
  onChange: (key: string, value: SettingsValue) => void
  presentation?: SettingsFieldPresentation
  optionLabels?: Readonly<Record<string, string>>
  invalid?: boolean
}

function FieldLabel({ presentation }: { presentation: SettingsFieldPresentation }) {
  return <>{presentation.label}{presentation.help ? <Info text={presentation.help} /> : null}</>
}

function EnumField({ descriptor, value, onChange, presentation, optionLabels }: SettingsFieldProps & { presentation: SettingsFieldPresentation }) {
  const selected = typeof value === 'string' ? value : null
  return (
    <Field inline label={<FieldLabel presentation={presentation} />}>
      <div className="set-pick" data-setting={descriptor.key}>
        <PickerRow
          label={presentation.label}
          options={descriptor.enumValues.map(option => ({ value: option, label: optionLabels?.[option] ?? settingsOptionLabel(option) }))}
          value={selected}
          onChange={next => onChange(descriptor.key, typeof next === 'string' ? next : null)}
          clearable={descriptor.nullable}
          emptyLabel="Unknown"
        />
      </div>
    </Field>
  )
}

function BooleanField({ descriptor, value, onChange, presentation }: SettingsFieldProps & { presentation: SettingsFieldPresentation }) {
  const unknown = typeof value !== 'boolean'
  return (
    <Field inline label={<FieldLabel presentation={presentation} />}>
      <span className="set-bool" data-setting={descriptor.key}>
        <Switch checked={value === true} onChange={checked => onChange(descriptor.key, checked)} label={unknown ? 'Unknown' : value ? 'Yes' : 'No'} />
        {descriptor.nullable && !unknown ? <Button small onClick={() => onChange(descriptor.key, null)} ariaLabel={`Clear ${presentation.label}`}>Clear</Button> : null}
      </span>
    </Field>
  )
}

function InputField({ descriptor, value, onChange, presentation, invalid }: SettingsFieldProps & { presentation: SettingsFieldPresentation }) {
  const id = useId()
  const [focused, setFocused] = useState(false)
  const display = settingsValueToDisplay(value, descriptor, presentation.kind)
  const money = presentation.kind === 'money' || presentation.kind === 'pence'
  const formatted = (money || presentation.kind === 'percent') && display !== '' ? formatSettingsMoney(display) : display
  const [draft, setDraft] = useState(formatted)
  useEffect(() => { if (!focused) setDraft(formatted) }, [formatted, focused])
  const unit = presentation.kind === 'percent' ? '%' : presentation.unit
  const change = (next: string) => { setDraft(next); onChange(descriptor.key, settingsDisplayToValue(next, descriptor, presentation.kind)) }
  const ariaLabel = `${presentation.label}${money ? ', pounds' : unit ? `, ${unit}` : ''}`
  return (
    <Field inline htmlFor={id} label={<FieldLabel presentation={presentation} />}>
      <span className="set-ctl" data-setting={descriptor.key} data-kind={presentation.kind} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}>
        {presentation.kind === 'date' ? <DateInput id={id} value={draft} onChange={change} ariaLabel={ariaLabel} invalid={invalid} />
          : presentation.kind === 'text' ? <TextInput id={id} value={draft} onChange={change} ariaLabel={ariaLabel} placeholder="Unknown" invalid={invalid} />
            : <NumberInput id={id} value={draft} onChange={change} ariaLabel={ariaLabel} placeholder="Unknown" invalid={invalid} prefix={money ? '£' : undefined} suffix={unit} />}
      </span>
    </Field>
  )
}

export function SettingsField(props: SettingsFieldProps) {
  const presentation = props.presentation ?? settingsFieldPresentation(props.descriptor)
  if (presentation.kind === 'enum') return <EnumField {...props} presentation={presentation} />
  if (presentation.kind === 'boolean') return <BooleanField {...props} presentation={presentation} />
  return <InputField {...props} presentation={presentation} />
}
